/**
 * Умное форматирование цены в зависимости от её значения
 */
export const formatPrice = (price: number | undefined | null): string => {
  if (price === undefined || price === null || isNaN(price)) {
    return '---';
  }

  const priceValue = Number(price);
  
  // Для очень маленьких цен (меньше 0.001)
  if (priceValue < 0.001 && priceValue > 0) {
    // Показываем до первых значимых цифр + 2 дополнительных
    const str = priceValue.toString();
    if (str.includes('e')) {
      // Научная нотация - показываем как есть
      return priceValue.toFixed(8).replace(/\.?0+$/, '');
    }
    
    // Находим первую значимую цифру после запятой
    const decimalPart = str.split('.')[1] || '';
    let significantIndex = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] !== '0') {
        significantIndex = i;
        break;
      }
    }
    
    // Показываем до первых значимых цифр + 2 дополнительных, но не более 8
    const decimals = Math.min(significantIndex + 3, 8);
    return priceValue.toFixed(decimals).replace(/\.?0+$/, '');
  }
  
  // Для цен от 0.001 до 1
  if (priceValue >= 0.001 && priceValue < 1) {
    return priceValue.toFixed(5).replace(/\.?0+$/, '');
  }
  
  // Для цен от 1 до 100
  if (priceValue >= 1 && priceValue < 100) {
    return priceValue.toFixed(3).replace(/\.?0+$/, '');
  }
  
  // Для цен от 100 до 10000
  if (priceValue >= 100 && priceValue < 10000) {
    return priceValue.toFixed(2).replace(/\.?0+$/, '');
  }
  
  // Для больших цен (от 10000)
  if (priceValue >= 10000) {
    return priceValue.toFixed(1).replace(/\.?0+$/, '');
  }
  
  // По умолчанию
  return priceValue.toFixed(2).replace(/\.?0+$/, '');
};

/**
 * Отображение цены точно как она приходит от сервера
 * Без дополнительного форматирования или округления
 */
export const formatPriceAsReceived = (price: number | undefined | null): string => {
  if (price === undefined || price === null || isNaN(price)) {
    return '---';
  }

  // Преобразуем в строку и отображаем как есть
  const priceValue = Number(price);
  const result = priceValue.toString();
  
  return result;
};

/**
 * Форматирование цены для графика (всегда 2 знака после запятой для читаемости)
 */
export const formatPriceForChart = (price: number | undefined | null): string => {
  if (price === undefined || price === null || isNaN(price)) {
    return '0.00';
  }
  
  const priceValue = Number(price);
  
  // Для очень маленьких цен показываем больше знаков
  if (priceValue < 0.01 && priceValue > 0) {
    return formatPrice(priceValue); // Используем умное форматирование
  }
  
  // Для остальных цен - стандартные 2 знака
  return priceValue.toFixed(2);
};

/**
 * Определение количества десятичных знаков для валютной пары
 */
export const getDecimalPlaces = (symbol: string): number => {
  // Пары с очень маленькими ценами
  const microPairs = ['SHIB', 'DOGE', 'PEPE', 'FLOKI', 'BONK'];
  if (microPairs.some(pair => symbol.includes(pair))) {
    return 8;
  }
  
  // Пары с маленькими ценами
  const smallPairs = ['ADA', 'XRP', 'TRX', 'MATIC', 'NEAR'];
  if (smallPairs.some(pair => symbol.includes(pair))) {
    return 5;
  }
  
  // Пары со средними ценами
  const mediumPairs = ['USDT', 'USDC', 'BUSD', 'DAI'];
  if (mediumPairs.some(pair => symbol.includes(pair))) {
    return 4;
  }
  
  // По умолчанию
  return 3;
};

/**
 * Подсчет количества знаков после запятой в числе
 * ПРОСТАЯ ЛОГИКА: просто считаем символы после точки
 */
export const countDecimalPlaces = (price: number): number => {
  if (!price || isNaN(price)) return 2;
  
  const str = price.toString();
  
  // Обработка научной нотации (например, 1e-8)
  if (str.includes('e')) {
    const parts = str.split('e');
    const exponent = parseInt(parts[1]);
    if (exponent < 0) {
      const result = Math.abs(exponent);
      return result;
    }
    return 0;
  }
  
  // Обычное число с десятичной точкой
  if (str.includes('.')) {
    const afterDot = str.split('.')[1];
    const result = afterDot.length;
    
    return result; // ВОТ И ВСЁ!
  }
  
  return 0;
};

/**
 * Автоматическое определение priceFormat на основе последней цены
 * Алгоритм: шаг сетки = 1 в младшем разряде цены, но с ограничениями для LightweightCharts
 */
export const getPriceFormatFromPrice = (lastPrice: number) => {
  // ИСПРАВЛЕНИЕ: Проверяем undefined/null СРАЗУ
  if (lastPrice === undefined || lastPrice === null || !lastPrice || isNaN(lastPrice)) {
    return {
      type: 'price' as const,
      precision: 2,
      minMove: 0.01
    };
  }
  
  // Считаем количество знаков после запятой
  const decimalPlaces = countDecimalPlaces(lastPrice);
  
  // ИСПРАВЛЕНИЕ: Ограничиваем precision для предотвращения ошибок LightweightCharts
  // LightweightCharts имеет ограничения на слишком большую точность
  let safePrecision = Math.min(decimalPlaces, 8); // Максимум 8 знаков
  let safeMinMove: number;
  
  // Если нет знаков после запятой, используем стандартные настройки
  if (decimalPlaces === 0) {
    safePrecision = 2;
    safeMinMove = 0.01;
  } else {
    // Шаг = 1 в младшем разряде, но с безопасными ограничениями
    safeMinMove = Math.pow(10, -safePrecision);
    
    // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: minMove не должен быть слишком маленьким
    // LightweightCharts может не справляться с очень маленькими значениями
    const MIN_ALLOWED_MOVE = 1e-8; // 0.00000001 - минимально допустимое значение
    if (safeMinMove < MIN_ALLOWED_MOVE) {
      safeMinMove = MIN_ALLOWED_MOVE;
      safePrecision = 8; // Соответствующая точность
    }
  }
  
  return {
    type: 'price' as const,
    precision: safePrecision,
    minMove: safeMinMove
  };
};

/**
 * Создает кастомный форматтер цен для LightweightChart
 * Округляет значения до указанного количества знаков после запятой
 */
export const createPriceFormatter = (decimalPlaces: number) => {
  // Валидация входных параметров
  const safePlaces = Math.max(0, Math.min(decimalPlaces || 2, 8)); // От 0 до 8 знаков
  
  return (price: number) => {
    // Строгая валидация входящего значения
    if (price === undefined || price === null || isNaN(price) || !isFinite(price)) {
      return '---';
    }
    
    // Обработка очень маленьких чисел (близких к нулю)
    if (Math.abs(price) < 1e-10) {
      return '0';
    }
    
    try {
    // Округляем до нужного количества знаков
      const rounded = Number(price.toFixed(safePlaces));
    const result = rounded.toString();
    
    return result;
    } catch (error) {
      return price.toString(); // Fallback - возвращаем как есть
    }
  };
};

/**
 * Форматирование цены для анимации с фиксированной шириной в 10 символов
 * Предотвращает изменение ширины элементов при анимации
 */
export const formatPriceForAnimation = (price: number | undefined | null, symbol?: string): string => {
  if (price === undefined || price === null || isNaN(price)) {
    return '0.00000000'; // Фиксированная ширина 10 символов
  }
  
  const priceValue = Number(price);
  
  // Определяем количество знаков в зависимости от величины цены
  // Цель: всегда получить строку длиной 10 символов
  let result: string;
  
  if (priceValue >= 1000000) {
    // Для очень больших цен: 1234567.89 (10 символов)
    result = priceValue.toFixed(2);
  } else if (priceValue >= 100000) {
    // Для больших цен: 123456.789 (10 символов)
    result = priceValue.toFixed(3);
  } else if (priceValue >= 10000) {
    // Для средних цен: 12345.6789 (10 символов)
    result = priceValue.toFixed(4);
  } else if (priceValue >= 1000) {
    // Для цен: 1234.56789 (10 символов)
    result = priceValue.toFixed(5);
  } else if (priceValue >= 100) {
    // Для цен: 123.456789 (10 символов)
    result = priceValue.toFixed(6);
  } else if (priceValue >= 10) {
    // Для цен: 12.3456789 (10 символов)
    result = priceValue.toFixed(7);
  } else if (priceValue >= 1) {
    // Для цен: 1.23456789 (10 символов)
    result = priceValue.toFixed(8);
  } else {
    // Для маленьких цен: 0.12345678 (10 символов)
    result = priceValue.toFixed(8);
  }
  
  // Обрезаем или дополняем до 10 символов
  if (result.length > 10) {
    result = result.substring(0, 10);
  } else if (result.length < 10) {
    // Дополняем нулями справа если нужно
    const decimalIndex = result.indexOf('.');
    if (decimalIndex !== -1) {
      const zerosNeeded = 10 - result.length;
      result = result + '0'.repeat(zerosNeeded);
    } else {
      // Если нет десятичной точки, добавляем её и нули
      const zerosNeeded = 10 - result.length - 1; // -1 для точки
      if (zerosNeeded > 0) {
        result = result + '.' + '0'.repeat(zerosNeeded);
      }
    }
  }
  
  return result;
}; 